import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ProfileHeader } from '../components/ProfileHeader';
import { BottomNav } from '../components/BottomNav';
import { MemoryGrid } from '../components/MemoryGrid';
import { AllMemoriesGrid } from '../components/AllMemoriesGrid';
import { TryLauncher } from '../components/try/TryLauncher';
import { SignatureReplay } from '../components/try/SignatureReplay';
import { AuraRing } from '../components/AuraRing';
import { useAuth } from '../contexts/AuthContext';
import { X, Camera, Grid3x3, LayoutGrid, ChevronDown, Sparkles, BookOpen, Shield } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { motion, AnimatePresence } from 'framer-motion';
import { BACKEND_URL } from '../utils/api';
import { ANIMAL_ICONS, EMPIRE_ICON } from '../theme';
import DragonGateway, { preloadMuseum } from '../components/museum/DragonGateway';
import { SovereignsPath } from '../components/try/SovereignsPath';
import { useNativeBack } from '../hooks/useHistoryClose';
import { clickVisibleBackButton } from '../utils/backStack';
import { TILE_GUIDE } from '../data/tileGuide';

const OBSIDIAN = '#05050A';

const TAG_COLORS = {
  fashion: '#D4AF37', food: '#C8A95E', explore: '#0F52BA',
  souls: '#DC143C', sporty: '#10B981', success: '#E5C44D', legacy: '#8B5CF6',
  art: '#046307', dragon: '#FFD700',
  fox: '#E8B830', panther: '#A9A6D6', peacock: '#EEF1FF',
};

/* 9 pillars a user can wear as their sigil — mirrors Discover.js order.
   Empire (dragon) is listed FIRST so users can pick the imperial mark itself. */
const TAG_PILLARS = [
  { id: 'dragon',  label: 'Empire'  },
  { id: 'fashion', label: 'Fashion' },
  { id: 'food',    label: 'Taste'   },
  { id: 'explore', label: 'Explore' },
  { id: 'souls',   label: 'Soul'    },
  { id: 'sporty',  label: 'Sport'   },
  { id: 'success', label: 'Success' },
  { id: 'legacy',  label: 'Legacy'  },
  { id: 'art',     label: 'Art'     },
];

/* ═══ PRONOIA BALANCE BOX ═══
   Displayed on the Empire profile beneath the Logout button. Border cycles
   through all 8 pillar colours in a continuous 12-second breathing loop
   (reuses the .bliss-world-beam keyframes defined in TryLauncher.jsx —
   keyframes are global via <style>). Font auto-scales to fit numbers up
   to 999,999,999 without overflow. Space above via margin-top. */
const PronoiaBalanceBox = ({ pronoia }) => {
  const formatted = Intl.NumberFormat('en-US').format(pronoia || 0);
  // Adaptive font-size — inspect length so 100M+ still fits inside the pill.
  const len = formatted.length;
  const fontSize = len >= 13 ? 11 : len >= 11 ? 12 : len >= 9 ? 13 : len >= 7 ? 15 : 17;
  return (
    <div
      className="bliss-world-beam mt-4 w-full rounded-xl px-2 py-2 flex flex-col items-center justify-center overflow-hidden"
      style={{
        background:
          'radial-gradient(ellipse at 30% 20%, rgba(197,160,89,0.08) 0%, rgba(0,0,0,0) 60%), ' +
          'linear-gradient(180deg, #0c0710 0%, #050308 100%)',
      }}
      data-testid="profile-pronoia-box"
      aria-label={`Pronoia count ${formatted}`}>
      <p className="font-['Outfit'] uppercase tracking-[0.24em] leading-none"
        style={{ color: '#C5A059', fontSize: 8 }}>
        Pronoia
      </p>
      <p className="font-['Outfit'] font-medium leading-none mt-1"
        style={{
          color: '#FFE9A0',
          fontSize,
          letterSpacing: '0.02em',
          textShadow: '0 0 8px rgba(197,160,89,0.45)',
          whiteSpace: 'nowrap',
        }}
        data-testid="profile-pronoia-count">
        {formatted}
      </p>
    </div>
  );
};

const AppDescription = () => {
  const [open, setOpen] = useState(false);
  /* ── ARSH PILLARS — Ascendant · Regal · Stellar · Heritage ── */
  const PILLARS = [
    { letter: 'A', name: 'Ascendant', text: 'We empower your rise — providing the instruments to elevate your lifestyle and your ambitions.' },
    { letter: 'R', name: 'Regal',     text: 'Every feature is forged to a standard of excellence, luxury, and Old-Money sophistication.' },
    { letter: 'S', name: 'Stellar',   text: 'Our technology is forward-thinking and advanced, ensuring your digital experience is unmatched.' },
    { letter: 'H', name: 'Heritage',  text: 'We preserve your story — so your legacy stands chronicled for the generations after you.' },
  ];
  return (
    <div className="mt-2" data-testid="app-description-section">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-3 px-4 rounded-xl border border-[#D4AF37]/10 hover:border-[#D4AF37]/20 transition-all duration-300"
        style={{ background: 'rgba(212,175,55,0.03)' }} data-testid="app-description-toggle">
        <div className="flex items-center gap-2">
          <Sparkles size={16} className="text-[#D4AF37]" />
          <span className="font-['Cormorant_Garamond'] text-sm font-semibold text-white/80">App Description</span>
        </div>
        <ChevronDown size={18} className={`text-white/30 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.4 }} className="overflow-hidden">
            <div className="mt-3 rounded-xl overflow-hidden border border-[#D4AF37]/10 relative" data-testid="app-description-content">
              {/* Dragon background */}
              <div className="absolute inset-0 opacity-[0.06] flex items-center justify-center pointer-events-none">
                <img src="/icons/empire_seal.png" alt="" className="w-48 h-48 object-contain" />
              </div>
              <div className="relative px-6 pt-6 pb-6 text-center space-y-4" style={{ background: 'linear-gradient(180deg, rgba(10,10,20,0.95) 0%, rgba(5,5,10,0.98) 100%)' }}>
                <p className="font-['Outfit'] text-[10px] tracking-[0.34em] uppercase text-[#D4AF37]/70">The Bliss Experience</p>
                <h3 className="font-['Cormorant_Garamond'] text-2xl text-[#D4AF37] font-semibold tracking-wide">The ARSH Identity</h3>
                <div className="h-[1px] bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />
                <p className="font-['Cormorant_Garamond'] italic text-sm text-[#F5E6A0]/85 leading-relaxed">
                  Bliss is the digital manifestation of the
                  <span className="text-[#D4AF37]"> ARSH Imperium Core</span> —
                  an ecosystem built upon the four foundational pillars of our brand.
                </p>
                <div className="h-[1px] bg-gradient-to-r from-transparent via-[#D4AF37]/15 to-transparent" />
                <div className="space-y-3 text-left">
                  {PILLARS.map((p) => (
                    <div key={p.letter} className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center"
                        style={{
                          background: 'radial-gradient(circle at 50% 30%, #F5E6A0 0%, #D4AF37 55%, #7A5418 100%)',
                          boxShadow: '0 0 10px rgba(212,175,55,0.45)',
                          color: '#1a0e02',
                          fontFamily: 'Cormorant Garamond, serif',
                          fontWeight: 700,
                          fontStyle: 'italic',
                          fontSize: 18,
                        }}>{p.letter}</div>
                      <div className="min-w-0 flex-1">
                        <h4 className="font-['Cormorant_Garamond'] text-sm text-[#F5D97A] font-semibold tracking-wider">{p.name}</h4>
                        <p className="font-['Cormorant_Garamond'] text-[12px] text-white/55 leading-relaxed mt-0.5">{p.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="h-[1px] bg-gradient-to-r from-transparent via-[#D4AF37]/15 to-transparent" />
                <p className="font-['Cormorant_Garamond'] italic text-[12px] text-white/45 leading-relaxed">
                  ~ Document your life with Bliss. Claim your legacy. ~
                </p>
                <div className="pt-1"><span className="font-['Cormorant_Garamond'] text-lg text-[#D4AF37] font-light tracking-[0.18em]">#ARSH</span></div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FeaturesDescription = () => {
  const [open, setOpen] = useState(false);
  /* ── Integrated Features Directory ── */
  const features = [
    { title: 'The 8 Pillars', text: 'Upload a vibe and tag it to Fashion, Taste, Explore, Soul, Sport, Success, Legacy or Art. It flows into your Vault, Sovereign, Essence and Mosaic.' },
    { title: 'Profile Sigil', text: 'Choose one of the 8 pillar animals (or the Empire dragon) as your identity mark on your profile and in Bliss Society.' },
    { title: 'Aura Ring', text: 'The 6 coloured lines around your photo light up as you fuse Power, Fuel, Force, Energy, Destiny and Aegis each day. The top line turns gold when you fuse all 6, or when you light your Pronoia ring.' },
    ...TILE_GUIDE.map((t) => ({ title: t.name, text: `${t.what} ${t.why}` })),
    { title: 'Bliss Society', text: 'The Society of Successful Empires: the golden Fox (Pronoia), black Panther (Aura) and white Peacock (Vibes) shields with their Top 10, Global Feeds, Global Users, Global Bliss Chat and the Latest Chronicles.' },
    { title: 'Global Bliss Chat', text: 'A live chat for everyone, with a new icon room every hour. You get 10 chat coins a day, and each hour\u2019s messages vanish for good when it ends.' },
    { title: 'Bliss Guide', text: 'Tap Menu on the Inscribe page for a full guide to the pillars, your sigil, the Aura ring, all 24 rituals and Bliss Society.' },
  ];
  return (
    <div className="mt-2" data-testid="features-description-section">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-3 px-4 rounded-xl border border-[#D4AF37]/10 hover:border-[#D4AF37]/20 transition-all duration-300"
        style={{ background: 'rgba(212,175,55,0.03)' }} data-testid="features-description-toggle">
        <div className="flex items-center gap-2">
          <BookOpen size={16} className="text-[#D4AF37]/70" />
          <span className="font-['Cormorant_Garamond'] text-sm font-semibold text-white/80">Features Description</span>
        </div>
        <ChevronDown size={18} className={`text-white/30 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.4 }} className="overflow-hidden">
            <div className="mt-3 rounded-xl overflow-hidden border border-[#D4AF37]/10 relative" data-testid="features-description-content">
              <div className="absolute inset-0 opacity-[0.04] flex items-center justify-center pointer-events-none">
                <img src="/icons/empire_seal.png" alt="" className="w-56 h-56 object-contain" />
              </div>
              <div className="relative px-5 pt-5 pb-5" style={{ background: 'linear-gradient(180deg, rgba(10,10,20,0.95) 0%, rgba(5,5,10,0.98) 100%)' }}>
                <p className="font-['Outfit'] text-[10px] tracking-[0.32em] uppercase text-[#D4AF37]/70 text-center mb-1">Integrated Features Directory</p>
                <p className="font-['Cormorant_Garamond'] italic text-[12px] text-white/50 text-center">— the {features.length} halls of the Imperium —</p>
                <div className="h-[1px] bg-gradient-to-r from-transparent via-[#D4AF37]/20 to-transparent my-4" />
                <div className="space-y-3">
                  {features.map((f, i) => (
                    <div key={f.title} className="flex items-start gap-3">
                      <span className="flex-shrink-0 font-['Outfit'] text-[10px] tracking-[0.18em] text-[#D4AF37]/55 mt-[3px] w-5 text-right">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <div className="min-w-0 flex-1">
                        <h4 className="font-['Cormorant_Garamond'] text-[13px] text-[#F5D97A] font-semibold tracking-wider leading-tight">
                          {f.title}
                        </h4>
                        <p className="font-['Cormorant_Garamond'] text-[11.5px] text-white/45 leading-relaxed mt-0.5">{f.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="h-[1px] bg-gradient-to-r from-transparent via-[#D4AF37]/15 to-transparent mt-4 mb-3" />
                <p className="text-center font-['Cormorant_Garamond'] italic text-[11px] text-white/35">
                  ~ every hall a chapter of your sovereign chronicle ~
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   EMPIRE PROFILE — Compact, fits in 55% of viewport height
   ═══════════════════════════════════════════════════════════════ */

const EmpireProfile = ({ user, onSettingsClick }) => {
  const { logout, updateProfile } = useAuth();
  const navigate = useNavigate();
  const { ANIMAL_ICONS } = require('../theme');
  const { ArshHeader } = require('../components/ArshHeader');
  // NotificationBell removed — reactions & comments (Vibefication) feature has been deprecated.
  const [tagPickerOpen, setTagPickerOpen] = useState(false);
  const [pendingTag, setPendingTag] = useState(user?.profile_tag || null);
  const [savingTag, setSavingTag] = useState(false);

  const CATS = ['fashion','food','explore','souls','sporty','success','legacy','art'];

  const openTagPicker = () => {
    setPendingTag(user?.profile_tag || null);
    setTagPickerOpen(true);
  };
  const confirmTag = async () => {
    if (!pendingTag || pendingTag === user?.profile_tag) { setTagPickerOpen(false); return; }
    try {
      setSavingTag(true);
      await updateProfile({ profile_tag: pendingTag });
      setTagPickerOpen(false);
    } catch (_) { /* silent — toast handled upstream */ }
    finally { setSavingTag(false); }
  };

  const currentTag = user?.profile_tag;
  const currentTagColor = TAG_COLORS[currentTag] || '#D4AF37';

  return (
    <div className="h-full flex flex-col" style={{ background: '#000' }}>
      <style>{`
        @keyframes tagFlicker {
          0%, 100% { box-shadow: 0 0 4px var(--tag-color); filter: brightness(0.85); }
          50% { box-shadow: 0 0 14px var(--tag-color), 0 0 28px var(--tag-color); filter: brightness(1.2); }
        }
        .tag-flicker { animation: tagFlicker 2s ease-in-out infinite; }
        /* Breathing 8-colour beam — shared with the BLISS WORLD tab so
           the Gems box and BLISS WORLD tab pulse in the same aesthetic. */
        @keyframes blissWorldBeam {
          0%   { border-color: #D4AF37; box-shadow: 0 0 12px rgba(212,175,55,0.55), inset 0 0 8px rgba(212,175,55,0.10); }
          12.5%{ border-color: #C8A95E; box-shadow: 0 0 12px rgba(200,169,94,0.55), inset 0 0 8px rgba(200,169,94,0.10); }
          25%  { border-color: #0F52BA; box-shadow: 0 0 14px rgba(15,82,186,0.60),  inset 0 0 8px rgba(15,82,186,0.10); }
          37.5%{ border-color: #DC143C; box-shadow: 0 0 14px rgba(220,20,60,0.60),  inset 0 0 8px rgba(220,20,60,0.10); }
          50%  { border-color: #10B981; box-shadow: 0 0 14px rgba(16,185,129,0.60), inset 0 0 8px rgba(16,185,129,0.10); }
          62.5%{ border-color: #E5C44D; box-shadow: 0 0 12px rgba(229,196,77,0.55), inset 0 0 8px rgba(229,196,77,0.10); }
          75%  { border-color: #8B5CF6; box-shadow: 0 0 14px rgba(139,92,246,0.60), inset 0 0 8px rgba(139,92,246,0.10); }
          87.5%{ border-color: #2A9BD8; box-shadow: 0 0 14px rgba(42,155,216,0.60), inset 0 0 8px rgba(42,155,216,0.10); }
          100% { border-color: #D4AF37; box-shadow: 0 0 12px rgba(212,175,55,0.55), inset 0 0 8px rgba(212,175,55,0.10); }
        }
        .bliss-world-beam {
          border: 2px solid #D4AF37;
          animation: blissWorldBeam 12s linear infinite;
        }
      `}</style>
      <div className="flex-shrink-0"><ArshHeader /></div>
      <div className="flex px-3 min-h-0" style={{ flex: '0 0 auto' }}>
        {/* LEFT — Scoreboard */}
        <div className="flex flex-col gap-1 w-[95px] flex-shrink-0 justify-center">
          {CATS.map((key) => {
            const icon = ANIMAL_ICONS[key];
            const count = user?.category_counts?.[key] || 0;
            return (
              <div key={key} className="flex items-center gap-1.5">
                <div className="w-[24px] h-[24px] rounded-full overflow-hidden flex-shrink-0 border border-[#D4AF37]/25">
                  {icon && <img src={icon.src} alt={key} className="w-full h-full object-cover" style={{ objectPosition: icon.pos }} />}
                </div>
                <div className="flex-1 min-w-0 leading-none">
                  <span className="font-['Outfit'] text-[10px] text-white/40 block truncate">{key === 'food' ? 'Taste' : (key.charAt(0).toUpperCase() + key.slice(1))}</span>
                  <span className="font-['Outfit'] text-[13px] text-[#D4AF37] font-bold">{count}</span>
                </div>
              </div>
            );
          })}
        </div>
        {/* CENTER — Profile */}
        <div className="flex-1 flex flex-col items-center justify-center text-center min-w-0 py-2">
          <div className="relative mb-2"
            style={{ width: 132, height: 132, flex: '0 0 auto' }}>
            <AuraRing size={132}>
              <div className="w-28 h-28 rounded-full overflow-hidden border-2 border-[#D4AF37]/30" style={{ boxShadow: '0 0 30px rgba(212,175,55,0.15)' }}>
                {user?.profile_picture ? (
                  <img src={user.profile_picture} alt={user.nickname} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#D4AF37]/30 to-[#05050A] flex items-center justify-center">
                    <span className="font-['Cormorant_Garamond'] text-4xl text-[#D4AF37]">{user?.nickname?.charAt(0).toUpperCase()}</span>
                  </div>
                )}
              </div>
            </AuraRing>
            <button
              onClick={onSettingsClick}
              data-testid="edit-profile-picture"
              aria-label="Change profile picture"
              style={{
                position: 'absolute',
                bottom: 6,
                right: 6,
                width: 32,
                height: 32,
                minWidth: 32,
                maxWidth: 32,
                borderRadius: '9999px',
                background: '#D4AF37',
                color: '#000',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
                border: '2px solid #04050A',
                boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
                zIndex: 2,
              }}>
              <Camera size={14} />
            </button>
          </div>
          <h2 className="font-['Cormorant_Garamond'] text-2xl font-light text-white tracking-wide" data-testid="profile-nickname">{user?.nickname}</h2>
          {user?.life_quote && <p className="font-['Cormorant_Garamond'] italic text-sm text-white/35 mt-1 max-w-[220px] line-clamp-2">"{user.life_quote}"</p>}
        </div>
        {/* RIGHT — Actions column */}
        <div className="flex flex-col gap-2 w-[95px] flex-shrink-0 items-stretch pt-1">
          {/* Profile Tag — tap to open sigil picker */}
          {currentTag && ANIMAL_ICONS[currentTag] && (
            <div className="flex justify-center">
              <button
                type="button"
                onClick={openTagPicker}
                aria-label="Change sigil"
                data-testid="profile-tag-open"
                className="tag-flicker w-9 h-9 rounded-full overflow-hidden focus:outline-none active:scale-95 transition-transform"
                style={{
                  '--tag-color': currentTagColor + '80',
                  border: `1.5px solid ${currentTagColor}55`,
                  background: 'rgba(0,0,0,0.5)',
                }}>
                <img src={ANIMAL_ICONS[currentTag].src} alt={currentTag}
                  className="w-full h-full object-cover"
                  style={{ objectPosition: ANIMAL_ICONS[currentTag].pos }} />
              </button>
            </div>
          )}

          {/* Settings — curved gold pill (moved down into old Logout slot) */}
          <button
            type="button"
            onClick={onSettingsClick}
            data-testid="profile-settings-button"
            className="flex items-center justify-center gap-1.5 w-full py-1.5 rounded-full active:scale-[0.97] transition-transform"
            style={{
              background: 'linear-gradient(135deg, rgba(212,175,55,0.10), rgba(212,175,55,0.02))',
              border: '1px solid rgba(212,175,55,0.45)',
              boxShadow: 'inset 0 0 8px rgba(212,175,55,0.05)',
            }}>
            <span className="text-[#D4AF37] flex-shrink-0">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
            </span>
            <span className="font-['Outfit'] text-[10px] uppercase tracking-[0.14em] text-white/85">Settings</span>
          </button>

          {/* Logout — matches Settings styling exactly (thin gold pill) */}
          <button
            type="button"
            onClick={async () => { await logout(); navigate('/login'); }}
            data-testid="profile-logout-button"
            className="flex items-center justify-center gap-1.5 w-full py-1.5 rounded-full active:scale-[0.97] transition-transform"
            style={{
              background: 'linear-gradient(135deg, rgba(212,175,55,0.10), rgba(212,175,55,0.02))',
              border: '1px solid rgba(212,175,55,0.45)',
              boxShadow: 'inset 0 0 8px rgba(212,175,55,0.05)',
            }}>
            <span className="text-[#D4AF37] flex-shrink-0">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
            </span>
            <span className="font-['Outfit'] text-[10px] uppercase tracking-[0.14em] text-white/85">Logout</span>
          </button>

          {/* GEMS BALANCE BOX ───────────────────────────────────────────
              Sits below Logout with a clear gap. Border continuously
              breathes through all 8 pillar colours (reuses the
              blissWorldBeam keyframes defined in TryLauncher.jsx — the
              class name is scoped globally). Font auto-scales so numbers
              up to ~999,999,999 all fit inside without overflow. */}
          <PronoiaBalanceBox pronoia={user?.pronoia ?? 0} />
        </div>
      </div>

      {/* ── Sigil Picker Modal (tap-to-change tag) ─────────────── */}
      <AnimatePresence>
        {tagPickerOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[180] flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.78)', backdropFilter: 'blur(6px)' }}
            onClick={() => setTagPickerOpen(false)}
            data-testid="sigil-picker-modal">
            <motion.div
              initial={{ y: 24, opacity: 0, scale: 0.96 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 16, opacity: 0, scale: 0.98 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-sm rounded-2xl p-5"
              style={{
                background: 'linear-gradient(165deg, #0E0E14, #05050A)',
                border: '1px solid rgba(212,175,55,0.55)',
                boxShadow: '0 0 40px rgba(212,175,55,0.35)',
              }}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-['Outfit'] text-[9px] uppercase tracking-[0.32em]" style={{ color: '#D4AF37' }}>
                    Choose Your Sigil
                  </p>
                  <h4 className="font-['Cormorant_Garamond'] text-xl leading-tight mt-0.5" style={{ color: '#F5D97A' }}>
                    Wear a Pillar
                  </h4>
                </div>
                <button type="button" onClick={() => setTagPickerOpen(false)}
                  className="w-8 h-8 rounded-full flex items-center justify-center"
                  style={{ border: '1px solid rgba(212,175,55,0.4)', color: '#D4AF37' }}
                  data-testid="sigil-picker-close">
                  <X size={13}/>
                </button>
              </div>

              <div className="mt-4 grid grid-cols-3 gap-2.5">
                {TAG_PILLARS.map((p) => {
                  const icon = ANIMAL_ICONS[p.id];
                  const colour = TAG_COLORS[p.id] || '#D4AF37';
                  const selected = pendingTag === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setPendingTag(p.id)}
                      data-testid={`sigil-option-${p.id}`}
                      className="relative flex flex-col items-center gap-1 py-2 rounded-xl active:scale-95 transition-transform"
                      style={{
                        background: selected
                          ? `linear-gradient(160deg, ${colour}30, ${colour}08)`
                          : 'rgba(255,255,255,0.02)',
                        border: `1.5px solid ${selected ? colour : 'rgba(212,175,55,0.22)'}`,
                        boxShadow: selected ? `0 0 14px ${colour}66` : 'none',
                      }}>
                      <div className="w-11 h-11 rounded-full overflow-hidden"
                        style={{ border: `1px solid ${colour}55`, background: 'rgba(0,0,0,0.5)' }}>
                        {icon && (
                          <img src={icon.src} alt={p.label}
                            className="w-full h-full object-cover"
                            style={{ objectPosition: icon.pos }}/>
                        )}
                      </div>
                      <span className="font-['Outfit'] text-[9px] uppercase tracking-[0.14em]"
                        style={{ color: selected ? colour : 'rgba(255,255,255,0.6)' }}>
                        {p.label}
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="mt-5 flex items-center gap-2 justify-end">
                <button type="button" onClick={() => setTagPickerOpen(false)}
                  className="px-4 py-2 rounded-full font-['Outfit'] uppercase text-[10px] tracking-[0.22em]"
                  style={{ border: '1px solid rgba(212,175,55,0.4)', color: 'rgba(212,175,55,0.9)' }}
                  data-testid="sigil-picker-cancel">
                  Cancel
                </button>
                <button type="button" onClick={confirmTag}
                  disabled={!pendingTag || pendingTag === currentTag || savingTag}
                  className="px-5 py-2 rounded-full font-['Outfit'] uppercase text-[10px] tracking-[0.22em]"
                  style={{
                    background: (!pendingTag || pendingTag === currentTag || savingTag)
                      ? 'rgba(212,175,55,0.15)'
                      : 'linear-gradient(135deg, #FFE9A8, #B88F2A)',
                    color: (!pendingTag || pendingTag === currentTag || savingTag) ? 'rgba(212,175,55,0.5)' : '#050507',
                    border: '1px solid rgba(212,175,55,0.7)',
                    boxShadow: (!pendingTag || pendingTag === currentTag || savingTag) ? 'none' : '0 0 14px rgba(212,175,55,0.55)',
                  }}
                  data-testid="sigil-picker-confirm">
                  {savingTag ? 'Sealing…' : 'Confirm'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   EMPIRE GRID — 7 Icons in U-curve, fills 45% of viewport
   ═══════════════════════════════════════════════════════════════ */
const EMPIRE_CATEGORIES = [
  { id: 'fashion', label: 'Fashion',  color: '#D4AF37' },
  { id: 'food',    label: 'Taste',    color: '#C8A95E' },
  { id: 'explore', label: 'Explore',  color: '#0F52BA' },
  { id: 'souls',   label: 'Soul',     color: '#DC143C' },
  { id: 'sporty',  label: 'Sport',    color: '#10B981' },
  { id: 'success', label: 'Success',  color: '#E5C44D' },
  { id: 'legacy',  label: 'Legacy',   color: '#8B5CF6' },
];

const EmpireGrid = ({ onTabChange }) => {
  const { ANIMAL_ICONS, EMPIRE_ICON } = require('../theme');
  const SZ = 64;
  const DRAGON_SZ = 110;
  const renderIcon = (cat) => {
    const icon = ANIMAL_ICONS[cat.id];
    return (
      <motion.button key={cat.id} onClick={() => onTabChange(cat.id)} className="flex flex-col items-center" whileTap={{ scale: 0.92 }} data-testid={`empire-icon-${cat.id}`}>
        <div className="empire-icon-circle rounded-full overflow-hidden" style={{ width: SZ, height: SZ, '--glow-color': cat.color + '80', border: `2px solid ${cat.color}50` }}>
          {icon && <img src={icon.src} alt={cat.label} className="w-full h-full object-cover" style={{ objectPosition: icon.pos }} />}
        </div>
        <span className="font-['Cormorant_Garamond'] text-[12px] font-semibold tracking-wider mt-1" style={{ color: cat.color }}>{cat.label}</span>
      </motion.button>
    );
  };
  // Layout: Row1(2) → Row2(1 + DRAGON + 1) → Row3(2) → Row4(1 centered)
  const r1 = EMPIRE_CATEGORIES.slice(0, 2);  // Fashion, Food
  const left = EMPIRE_CATEGORIES[2];           // Explore
  const right = EMPIRE_CATEGORIES[3];          // Souls
  const r3 = EMPIRE_CATEGORIES.slice(4, 6);   // Sporty, Success
  const r4 = EMPIRE_CATEGORIES[6];             // Legacy

  return (
    <div className="h-full flex flex-col items-center justify-evenly px-1">
      <style>{`
        @keyframes empireIconPulse { 0%, 100% { box-shadow: 0 0 6px var(--glow-color); filter: brightness(0.9); } 50% { box-shadow: 0 0 18px var(--glow-color), 0 0 36px var(--glow-color); filter: brightness(1.15); } }
        @keyframes empireIconSpin { from { transform: perspective(400px) rotateY(0deg); } to { transform: perspective(400px) rotateY(360deg); } }
        .empire-icon-circle { animation: empireIconPulse 3s ease-in-out infinite, empireIconSpin 10s linear infinite; }
        @keyframes dragonFlicker { 
          0%, 100% { filter: brightness(1) drop-shadow(0 0 8px rgba(212,175,55,0.3)); } 
          15% { filter: brightness(1.3) drop-shadow(0 0 16px rgba(212,175,55,0.7)); }
          30% { filter: brightness(1) drop-shadow(0 0 6px rgba(15,82,186,0.5)); }
          45% { filter: brightness(1.2) drop-shadow(0 0 20px rgba(15,82,186,0.8)); }
          60% { filter: brightness(1.1) drop-shadow(0 0 12px rgba(212,175,55,0.6)); }
          75% { filter: brightness(1.35) drop-shadow(0 0 24px rgba(15,82,186,0.6)) drop-shadow(0 0 12px rgba(212,175,55,0.4)); }
          90% { filter: brightness(1) drop-shadow(0 0 8px rgba(212,175,55,0.4)); }
        }
        .dragon-center { animation: dragonFlicker 3s ease-in-out infinite; }
      `}</style>
      {/* Row 1: Fashion + Food */}
      <div className="flex w-full justify-evenly">{r1.map(renderIcon)}</div>
      {/* Row 2: Explore + DRAGON + Souls */}
      <div className="flex w-full items-center justify-evenly">
        {renderIcon(left)}
        <div className="dragon-center rounded-full overflow-hidden" style={{
          width: DRAGON_SZ, height: DRAGON_SZ,
          border: '2px solid rgba(212,175,55,0.4)',
          boxShadow: '0 0 30px rgba(212,175,55,0.2), 0 0 60px rgba(15,82,186,0.15)',
        }}>
          <img src={EMPIRE_ICON.src} alt="Dragon" className="w-full h-full object-cover" style={{ objectPosition: EMPIRE_ICON.pos }} />
        </div>
        {renderIcon(right)}
      </div>
      {/* Row 3: Sporty + Success */}
      <div className="flex w-full justify-evenly">{r3.map(renderIcon)}</div>
      {/* Row 4: Legacy centered */}
      <div className="flex w-full justify-center">{renderIcon(r4)}</div>
    </div>
  );
};

export default function Home() {
  const [activeTab, setActiveTab] = useState('empire');
  const [visitedTabs, setVisitedTabs] = useState(new Set(['empire'])); // ⚡ cache visited tabs
  const [showSettings, setShowSettings] = useState(false);
  const [showSovereignPath, setShowSovereignPath] = useState(false);
  const [viewMode, setViewMode] = useState('category');
  const [tryNotifCount, setTryNotifCount] = useState(0);
  const [tabDirection, setTabDirection] = useState(0);
  const [museumOpen, setMuseumOpen] = useState(false);
  const [museumInitialRoom, setMuseumInitialRoom] = useState('hall');
  const [dragonOriginRect, setDragonOriginRect] = useState(null);
  const { user, updateProfile, checkAuth } = useAuth();

  // Phone back: overlays close first, then any tab returns to Empire.
  useNativeBack(activeTab !== 'empire', () => { if (!clickVisibleBackButton()) handleTabChange('empire'); });
  useNativeBack(showSettings, () => setShowSettings(false));
  useNativeBack(showSovereignPath, () => setShowSovereignPath(false));
  useNativeBack(museumOpen, () => { if (!clickVisibleBackButton()) setMuseumOpen(false); });

  // Live Pronoia refresh — a new Universe-support log refetches /auth/me.
  useEffect(() => {
    const handler = () => {
      try { checkAuth(); } catch (_) { /* noop */ }
    };
    window.addEventListener('pronoia:logged', handler);
    return () => window.removeEventListener('pronoia:logged', handler);
  }, [checkAuth]);

  // ── Launch-time Signature intro ─────────────────────────────
  //   When a user with a sealed signature opens Bliss (fresh session),
  //   play the full-screen SignatureReplay once from the centre of the
  //   screen before revealing the Empire page. Uses sessionStorage so it
  //   only fires ONCE per app-launch — not on in-app navigation.
  const [signatureIntroActive, setSignatureIntroActive] = useState(() => {
    try {
      if (sessionStorage.getItem('sig_intro_played_v1') === '1') return false;
    } catch (_) { /* sessionStorage blocked */ }
    return true; // eligibility re-checked after `user` loads
  });
  // Empire remains hidden under a black cover until we either:
  //  (a) know the user has NO signature → reveal immediately, or
  //  (b) the SignatureReplay overlay is on screen covering everything, or
  //  (c) the user dismisses the intro.
  // This eliminates the profile → intro → profile flicker.
  const [revealEmpire, setRevealEmpire] = useState(() => {
    try {
      return sessionStorage.getItem('sig_intro_played_v1') === '1';
    } catch (_) { return false; }
  });
  useEffect(() => {
    if (!signatureIntroActive) { setRevealEmpire(true); return; }
    // Wait until the FULL profile from /api/auth/me has loaded — the raw
    // login response does NOT include signature_strokes / signature_data_url
    // (only /api/auth/me populates those). The `pronoia` field is set on every
    // /api/auth/me via setdefault(0) and is NEVER present in the login
    // response, so `typeof user.pronoia === 'number'` is the definitive signal
    // that the full user doc has arrived and the signature check is trustworthy.
    if (!user || typeof user.pronoia !== 'number') return;
    // Full profile in hand — if there's genuinely no sealed signature, dismiss.
    if (!user.signature_strokes && !user.signature_data_url) {
      setSignatureIntroActive(false);
      setRevealEmpire(true);
      try { sessionStorage.setItem('sig_intro_played_v1', '1'); } catch (_) { /* noop */ }
    }
  }, [user, signatureIntroActive]);
  const dismissSignatureIntro = useCallback(() => {
    try { sessionStorage.setItem('sig_intro_played_v1', '1'); } catch (_) { /* noop */ }
    setSignatureIntroActive(false);
    setRevealEmpire(true);
  }, []);

  const TAB_ORDER = ['empire', 'fashion', 'food', 'explore', 'souls', 'sporty', 'success', 'legacy', 'try'];

  const fetchTryNotifCount = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/try-notifications/unread-count`);
      setTryNotifCount(data.count || 0);
    } catch (err) {}
  }, []);

  useEffect(() => {
    fetchTryNotifCount();
    const interval = setInterval(fetchTryNotifCount, 30000);
    return () => clearInterval(interval);
  }, [fetchTryNotifCount]);

  // Warm the heavy 3D Museum bundle as soon as Home mounts so the very
  // first dragon-tap or door-tap lands instantly instead of waiting on
  // a webpack chunk download.
  useEffect(() => {
    const idle = window.requestIdleCallback
      ? window.requestIdleCallback(() => preloadMuseum(), { timeout: 1500 })
      : setTimeout(() => preloadMuseum(), 600);
    return () => {
      if (window.cancelIdleCallback && typeof idle === 'number') {
        window.cancelIdleCallback(idle);
      } else {
        clearTimeout(idle);
      }
    };
  }, []);

  // Deep-link: ?museum={roomId} → open the Museum directly at that hall.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const room = params.get('museum');
      if (room) {
        setMuseumInitialRoom(room);
        setMuseumOpen(true);
        // Clear the query so refresh doesn't keep re-opening
        const url = new URL(window.location.href);
        url.searchParams.delete('museum');
        url.searchParams.delete('u');
        window.history.replaceState({}, '', url.toString());
      }
    } catch (_) {}
  }, []);

  const handleTabChange = (newTab, opts = {}) => {
    if (newTab === activeTab) return;
    const oldIdx = TAB_ORDER.indexOf(activeTab);
    const newIdx = TAB_ORDER.indexOf(newTab);
    setTabDirection(newIdx > oldIdx ? 1 : -1);
    setActiveTab(newTab);
    // Mark tab as visited so we can render-and-cache it
    setVisitedTabs(prev => prev.has(newTab) ? prev : new Set([...prev, newTab]));
    setViewMode('category');
    if (newTab === 'try' && tryNotifCount > 0) {
      axios.post(`${BACKEND_URL}/api/try-notifications/mark-read`).catch(() => {});
      setTryNotifCount(0);
    }
    // Push a history entry so Android hardware back walks tab-by-tab.
    // Skip when this change was itself triggered BY popstate (opts.fromPop)
    // to avoid re-pushing what we just popped.
    if (!opts.fromPop) {
      try { window.history.pushState({ __homeTab: newTab }, ''); } catch (_) {}
    }
  };

  // Sync active-tab with the browser/Android history stack so hardware
  // back walks tab-by-tab (Instagram behaviour) before leaving Home.
  useEffect(() => {
    // Mark the initial Home state so we can detect "top of Home stack".
    try {
      const s = window.history.state;
      if (!s || !s.__homeTab) {
        window.history.replaceState({ __homeTab: 'empire' }, '');
      }
    } catch (_) {}
    const onPop = (e) => {
      const s = e.state;
      if (!s || !s.__homeTab) return;
      // Sync active tab from popped state without re-pushing to history.
      // Functional setState avoids stale closure on activeTab.
      setActiveTab((prev) => {
        if (prev === s.__homeTab) return prev;
        const oldIdx = TAB_ORDER.indexOf(prev);
        const newIdx = TAB_ORDER.indexOf(s.__homeTab);
        setTabDirection(newIdx > oldIdx ? 1 : -1);
        return s.__homeTab;
      });
      setVisitedTabs((prev) => prev.has(s.__homeTab) ? prev : new Set([...prev, s.__homeTab]));
      setViewMode('category');
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const tabVariants = {
    enter: (dir) => ({ x: dir > 0 ? 60 : -60, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir) => ({ x: dir > 0 ? -40 : 40, opacity: 0 }),
  };

  // ⚡ INSTANT TAB SWITCHING — all visited tabs stay mounted, hidden via display:none.
  // Returning to a tab shows cached data instantly (no re-fetch, no re-mount).
  const renderCachedTabs = () => {
    const tabs = [];
    visitedTabs.forEach(tab => {
      if (tab === 'empire' || tab === 'try') return; // handled separately
      const active = tab === activeTab;
      tabs.push(
        <div key={`${tab}-category`} style={{ display: active && viewMode === 'category' ? 'block' : 'none' }}>
          <MemoryGrid category={tab} />
        </div>
      );
      tabs.push(
        <div key={`${tab}-all`} style={{ display: active && viewMode === 'all' ? 'block' : 'none' }}>
          <AllMemoriesGrid />
        </div>
      );
    });
    // Try tab (stays mounted once visited).  Wrapper must be a flex column
    // with a definite height so TryLauncher's internal `h-full` stretches the
    // SponsoredNativeAd slot to fill the full available viewport below the
    // launcher grid (otherwise the ad collapses to ~200px of content height).
    if (visitedTabs.has('try')) {
      const tryActive = activeTab === 'try';
      tabs.push(
        <div key="try" style={{
          display: tryActive ? 'flex' : 'none',
          flexDirection: 'column',
          height: '100%',
        }}>
          <TryLauncher active={tryActive && !museumOpen && !showSettings && !showSovereignPath} onOpenMuseum={(roomId) => { setDragonOriginRect(null); setMuseumInitialRoom(roomId || 'hall'); setMuseumOpen(true); }} />
        </div>
      );
    }
    return tabs;
  };

  return (
    <div className={activeTab === 'empire' ? 'fixed inset-0 overflow-hidden' : 'app-viewport relative overflow-hidden'} style={{ background: '#000000' }}>
      {/* Boot cover — keeps the Empire hidden until either the signature
          intro is on screen or we've confirmed the user has no signature.
          Sits at z=100 (above Empire content, below SignatureReplay z-200). */}
      {!revealEmpire && (
        <div
          aria-hidden
          data-testid="empire-boot-cover"
          style={{ position: 'fixed', inset: 0, background: '#000', zIndex: 100 }}
        />
      )}
      {/* ═══ Launch-time Signature Intro ═══
          Full-screen cinematic replay that plays ONCE per app-launch when
          the user has a sealed signature. Uses sessionStorage to skip on
          in-app navigation / tab-switches. Reveals Empire beneath on close. */}
      {signatureIntroActive && user && (user.signature_strokes || user.signature_data_url) && (
        <SignatureReplay
          strokes={user.signature_strokes}
          fallbackImage={user.signature_data_url}
          onClose={dismissSignatureIntro}
        />
      )}
      {/* Viewport height CSS — 100dvh with 100vh fallback */}
      <style>{`
        .app-viewport { height: 100vh; height: 100dvh; }
      `}</style>
      {/* Gold Dust Particles — only on Empire tab */}
      {activeTab === 'empire' && (
        <>
          <style>{`
            @keyframes goldDustFloat {
              0%   { transform: translateY(0) translateX(0) scale(0); opacity: 0; }
              10%  { opacity: 1; transform: translateY(-10vh) translateX(5px) scale(1); }
              90%  { opacity: 0.6; }
              100% { transform: translateY(-100vh) translateX(-10px) scale(0.3); opacity: 0; }
            }
            .gold-dust {
              position: fixed;
              width: 3px;
              height: 3px;
              border-radius: 50%;
              background: #D4AF37;
              pointer-events: none;
              z-index: 5;
              animation: goldDustFloat linear infinite;
            }
          `}</style>
          {Array.from({ length: 10 }).map((_, i) => (
            <div
              key={i}
              className="gold-dust"
              style={{
                left: `${5 + (i * 5.3) % 90}%`,
                bottom: `${-5 - (i * 7) % 20}%`,
                animationDuration: `${4 + (i % 5) * 1.5}s`,
                animationDelay: `${(i * 0.6) % 8}s`,
                width: `${2 + (i % 3)}px`,
                height: `${2 + (i % 3)}px`,
                boxShadow: `0 0 ${4 + (i % 3) * 2}px rgba(212,175,55,0.4)`,
              }}
            />
          ))}
        </>
      )}

      <div className="relative z-10" style={activeTab === 'empire' ? { position: 'absolute', top: 0, left: 0, right: 0, bottom: 85, display: 'flex', flexDirection: 'column', overflow: 'hidden' } : { height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* ═══ EMPIRE TAB — Fixed, absolutely no scroll ═══ */}
        {activeTab === 'empire' && (
          <>
            <div style={{ flex: '0 0 auto' }}>
              <EmpireProfile user={user} onSettingsClick={() => setShowSettings(true)} />
            </div>
            {/* Empire Constellation — 8 icons in fixed positions, each spinning on its own Y-axis */}
            <div style={{ flex: '1 1 0', minHeight: 0, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4px 12px 12px' }}>
              <style>{`
                @keyframes spinAxisY {
                  from { transform: rotateY(0deg); }
                  to   { transform: rotateY(360deg); }
                }
                @keyframes colorPulse {
                  0%, 100% {
                    box-shadow: 0 0 5px var(--glow), 0 0 10px var(--glow);
                    filter: brightness(1);
                  }
                  50% {
                    box-shadow: 0 0 10px var(--glow), 0 0 20px var(--glow);
                    filter: brightness(1.12);
                  }
                }
                @keyframes dragonBreath {
                  0%, 100% { box-shadow: 0 0 18px rgba(212,175,55,0.45), 0 0 42px rgba(15,82,186,0.25), inset 0 0 10px rgba(212,175,55,0.25); }
                  50%      { box-shadow: 0 0 34px rgba(212,175,55,0.85), 0 0 70px rgba(15,82,186,0.5),  inset 0 0 14px rgba(212,175,55,0.4); }
                }
                @keyframes gearSpin {
                  from { transform: translate(-50%, -50%) rotate(0deg); }
                  to   { transform: translate(-50%, -50%) rotate(360deg); }
                }
                @keyframes gearSpinReverse {
                  from { transform: translate(-50%, -50%) rotate(360deg); }
                  to   { transform: translate(-50%, -50%) rotate(0deg); }
                }
                @keyframes constellationDrift {
                  0%, 100% { opacity: 0.35; }
                  50%      { opacity: 0.55; }
                }
              `}</style>
              <div
                data-testid="empire-rosette"
                style={{
                  position: 'relative',
                  width: '100%', maxWidth: 360,
                  height: '100%', maxHeight: 440,
                  aspectRatio: '0.92',
                  margin: '0 auto',
                  perspective: 800,
                }}
              >
                {(() => {
                  // Heptagon layout — 7 icons evenly spaced around center (0°=top, clockwise)
                  const CENTER_X = 50;
                  const CENTER_Y = 46;
                  const RADIUS = 40; // % of container
                  const baseItems = [
                    { id: 'fashion', label: 'Fashion', color: '#D4AF37' },
                    { id: 'food',    label: 'Taste',   color: '#E8D88C' },
                    { id: 'souls',   label: 'Soul',   color: '#DC143C' },
                    { id: 'art',     label: 'Art',     color: '#1E6FE3' },
                    { id: 'success', label: 'Success', color: '#C0C0C0' }, // stallion → silver glow
                    { id: 'legacy',  label: 'Legacy',  color: '#8B5CF6' },
                    { id: 'sporty',  label: 'Sport',  color: '#10B981' },
                    { id: 'explore', label: 'Explore', color: '#0F52BA' },
                  ];
                  const items = baseItems.map((it, i) => {
                    const theta = (i * (360 / baseItems.length)) * Math.PI / 180;
                    const x = CENTER_X + RADIUS * Math.sin(theta);
                    const y = CENTER_Y - RADIUS * Math.cos(theta);
                    return { ...it, x, y, size: 62, theta };
                  });
                  // Roman numerals around gear (12 positions)
                  const ROMAN = ['XII','I','II','III','IV','V','VI','VII','VIII','IX','X','XI'];
                  return (
                    <>
                      {/* Radial spokes from gear edge to each icon */}
                      <svg
                        viewBox="0 0 100 100"
                        preserveAspectRatio="none"
                        style={{
                          position: 'absolute', inset: 0, width: '100%', height: '100%',
                          pointerEvents: 'none',
                          opacity: 0.6,
                        }}
                      >
                        {items.map((cat, i) => {
                          const x1 = CENTER_X + 22 * Math.sin(cat.theta);
                          const y1 = CENTER_Y - 22 * Math.cos(cat.theta);
                          const x2 = CENTER_X + 32 * Math.sin(cat.theta);
                          const y2 = CENTER_Y - 32 * Math.cos(cat.theta);
                          return (
                            <line key={`spoke-${i}`}
                              x1={x1} y1={y1} x2={x2} y2={y2}
                              stroke="#D4AF37" strokeWidth="0.35" opacity="0.7"
                              strokeLinecap="round"
                            />
                          );
                        })}
                        {/* Faint heptagon outline connecting the icons */}
                        <polygon
                          points={items.map(c => `${CENTER_X + (RADIUS - 6) * Math.sin(c.theta)},${CENTER_Y - (RADIUS - 6) * Math.cos(c.theta)}`).join(' ')}
                          fill="none" stroke="#D4AF37" strokeWidth="0.2"
                          strokeDasharray="1 1.8" opacity="0.35"
                        />
                      </svg>
                      {/* Ornate gear — outer toothed ring with Roman numerals */}
                      <div
                        aria-hidden
                        style={{
                          position: 'absolute',
                          left: '50%', top: '46%',
                          width: 210, height: 210,
                          transform: 'translate(-50%, -50%)',
                          animation: 'gearSpin 50s linear infinite',
                          pointerEvents: 'none',
                          zIndex: 1,
                        }}
                      >
                        <svg viewBox="0 0 100 100" style={{ width: '100%', height: '100%', overflow: 'visible' }}>
                          <defs>
                            <radialGradient id="gearGold" cx="50%" cy="50%" r="50%">
                              <stop offset="0%" stopColor="#F5D97A" stopOpacity="0.95" />
                              <stop offset="60%" stopColor="#D4AF37" stopOpacity="0.75" />
                              <stop offset="100%" stopColor="#8B6B1F" stopOpacity="0.35" />
                            </radialGradient>
                          </defs>
                          {/* Outer toothed gear — ~32 teeth */}
                          <circle cx="50" cy="50" r="47" fill="none" stroke="url(#gearGold)"
                                  strokeWidth="3" strokeDasharray="1 2" opacity="0.9" />
                          {/* Roman numeral ring */}
                          <circle cx="50" cy="50" r="42" fill="none" stroke="#D4AF37"
                                  strokeWidth="0.35" opacity="0.8" />
                          {ROMAN.map((r, i) => {
                            const a = (i * 30) * Math.PI / 180;
                            const rx = 50 + 42 * Math.sin(a);
                            const ry = 50 - 42 * Math.cos(a);
                            return (
                              <text key={r} x={rx} y={ry}
                                fill="#D4AF37" fontSize="2.6" fontWeight="600"
                                fontFamily="Cormorant Garamond, serif"
                                textAnchor="middle" dominantBaseline="central"
                                opacity="0.85"
                              >{r}</text>
                            );
                          })}
                          {/* Tick marks ring */}
                          <circle cx="50" cy="50" r="38" fill="none" stroke="#D4AF37"
                                  strokeWidth="1.8" strokeDasharray="0.3 2.2" opacity="0.65" />
                          {/* Inner gold ring */}
                          <circle cx="50" cy="50" r="34" fill="none" stroke="#D4AF37" strokeWidth="0.6" opacity="0.75" />
                          <circle cx="50" cy="50" r="30" fill="none" stroke="#D4AF37" strokeWidth="0.3" opacity="0.5" />
                        </svg>
                      </div>
                      {/* Counter-rotating inner delicate ring */}
                      <div
                        aria-hidden
                        style={{
                          position: 'absolute',
                          left: '50%', top: '46%',
                          width: 140, height: 140,
                          transform: 'translate(-50%, -50%)',
                          animation: 'gearSpinReverse 32s linear infinite',
                          pointerEvents: 'none',
                          zIndex: 1,
                        }}
                      >
                        <svg viewBox="0 0 100 100" style={{ width: '100%', height: '100%', overflow: 'visible' }}>
                          <circle cx="50" cy="50" r="44" fill="none" stroke="#F5D97A"
                                  strokeWidth="0.5" strokeDasharray="3 2" opacity="0.55" />
                          <circle cx="50" cy="50" r="36" fill="none" stroke="#D4AF37" strokeWidth="0.4" opacity="0.5" />
                        </svg>
                      </div>
                      {items.map((cat, i) => {
                        const ic = ANIMAL_ICONS[cat.id];
                        return (
                          <button
                            key={cat.id}
                            type="button"
                            onClick={() => handleTabChange(cat.id)}
                            data-testid={`empire-icon-${cat.id}`}
                            style={{
                              all: 'unset', cursor: 'pointer',
                              position: 'absolute',
                              left: `${cat.x}%`, top: `${cat.y}%`,
                              transform: 'translate(-50%, -50%)',
                              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                              WebkitTapHighlightColor: 'transparent',
                            }}
                          >
                            <div
                              style={{
                                width: cat.size, height: cat.size, borderRadius: '50%', overflow: 'hidden',
                                border: `2px solid ${cat.color}`,
                                background: `radial-gradient(circle at 50% 30%, ${cat.color}22, #050308 72%)`,
                                '--glow': cat.color,
                                animation: `spinAxisY 9s linear infinite, colorPulse 3.5s ease-in-out infinite`,
                                transformStyle: 'preserve-3d',
                                willChange: 'transform, box-shadow, filter',
                              }}
                            >
                              {ic && (
                                <img
                                  src={ic.src}
                                  alt={cat.label}
                                  style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: ic.pos }}
                                />
                              )}
                            </div>
                            <span
                              style={{
                                fontFamily: 'Cormorant Garamond, serif',
                                fontSize: 12, fontWeight: 700, color: cat.color,
                                letterSpacing: '0.1em', whiteSpace: 'nowrap',
                                textShadow: '0 1px 4px rgba(0,0,0,0.85)',
                              }}
                            >
                              {cat.label}
                            </span>
                          </button>
                        );
                      })}
                      {/* Central Dragon — circle, breathing gold+blue glow. Tap to open Museum. */}
                      <button
                        type="button"
                        onClick={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setDragonOriginRect(rect);
                          setMuseumInitialRoom('hall');
                          setMuseumOpen(true);
                        }}
                        data-testid="empire-dragon-portal"
                        style={{
                          all: 'unset', cursor: 'pointer',
                          position: 'absolute',
                          left: '50%', top: '46%', transform: 'translate(-50%, -50%)',
                          width: 108, height: 108, borderRadius: '50%', overflow: 'hidden',
                          border: '2.5px solid #D4AF37',
                          background: 'radial-gradient(circle at 50% 30%, #1a130a, #050308 70%)',
                          animation: 'dragonBreath 3s ease-in-out infinite',
                          WebkitTapHighlightColor: 'transparent',
                          zIndex: 2,
                        }}
                      >
                        <img
                          src={EMPIRE_ICON?.src || '/icons/empire_seal.png'}
                          alt="Try"
                          style={{
                            width: '100%', height: '100%', objectFit: 'cover',
                            objectPosition: EMPIRE_ICON?.pos || 'center',
                            filter: 'drop-shadow(0 0 6px rgba(212,175,55,0.5))',
                          }}
                        />
                      </button>
                    </>
                  );
                })()}
              </div>
            </div>
          </>
        )}

        {/* View Mode Toggle — only on category tabs */}
        {activeTab !== 'try' && activeTab !== 'empire' && (
          <div
            className="flex-shrink-0 border-b border-[#D4AF37]/10 px-4 py-2"
            style={{ background: '#05050A', zIndex: 20 }}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setViewMode('category')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full font-['Outfit'] text-xs font-medium transition-all duration-300 ${
                    viewMode === 'category'
                      ? 'bg-[#D4AF37] text-black shadow-[0_0_15px_rgba(212,175,55,0.3)]'
                      : 'border border-white/10 text-white/40'
                  }`}
                  data-testid="category-view-button"
                >
                  <LayoutGrid size={14} />
                  Category View
                </button>
                <button
                  onClick={() => setViewMode('all')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full font-['Outfit'] text-xs font-medium transition-all duration-300 ${
                    viewMode === 'all'
                      ? 'bg-[#D4AF37] text-black shadow-[0_0_15px_rgba(212,175,55,0.3)]'
                      : 'border border-white/10 text-white/40'
                  }`}
                  data-testid="all-view-button"
                >
                  <Grid3x3 size={14} />
                  All Vibes
                </button>
              </div>

              {/* Sovereign's Path (global) — royal-purple book icon.  Opens
                  the all-pillars volume with the user's profile as the
                  cover (no pillar corners). */}
              <motion.button
                whileHover={{ y: -1 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowSovereignPath(true)}
                data-testid="sovereign-path-button"
                aria-label="Open Sovereign's Path"
                className="relative flex items-center gap-1.5 pl-2 pr-3 py-1.5 rounded-full font-['Cormorant_Garamond'] italic text-xs"
                style={{
                  background: 'linear-gradient(135deg, rgba(139,92,246,0.25), rgba(45,20,80,0.55))',
                  border: '1px solid rgba(176,140,255,0.55)',
                  color: '#E4D3FF',
                  boxShadow: '0 0 12px rgba(139,92,246,0.35), inset 0 0 8px rgba(139,92,246,0.18)',
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <defs>
                    <linearGradient id="sp-book-grad" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%"  stopColor="#E4D3FF" />
                      <stop offset="55%" stopColor="#8B5CF6" />
                      <stop offset="100%" stopColor="#3A1A70" />
                    </linearGradient>
                  </defs>
                  <path d="M4 4.5 C4 3.7 4.7 3 5.5 3 H18.5 C19.3 3 20 3.7 20 4.5 V19.5 C20 20.3 19.3 21 18.5 21 H5.5 C4.7 21 4 20.3 4 19.5 Z"
                        stroke="url(#sp-book-grad)" strokeWidth="1.4" fill="rgba(10,6,20,0.6)"/>
                  <path d="M8 6 H16 M8 9 H16 M8 12 H16" stroke="url(#sp-book-grad)" strokeWidth="0.9" strokeLinecap="round" opacity="0.7"/>
                  <path d="M11.5 3 V15 L12.5 13.5 L13.5 15 V3" fill="#B08CFF" stroke="#E4D3FF" strokeWidth="0.4"/>
                  <circle cx="12" cy="18" r="1.15" fill="url(#sp-book-grad)"/>
                </svg>
                <span className="tracking-wide">Sovereign&apos;s Path</span>
              </motion.button>
            </div>
          </div>
        )}

        {activeTab !== 'empire' && (
          <main
            className={`flex-1 ${activeTab === 'try' ? 'overflow-hidden' : 'overflow-y-auto'}`}
            style={{ paddingBottom: activeTab === 'try' ? 85 : 100 }}
          >
            {/* ⚡ Cached tabs — no re-mount, no re-fetch on switch */}
            {renderCachedTabs()}
          </main>
        )}

        <BottomNav activeTab={activeTab} onTabChange={handleTabChange} tryNotifCount={tryNotifCount} />

        {showSettings && <SettingsModal user={user} onClose={() => setShowSettings(false)} updateProfile={updateProfile} />}
      </div>
      {/* Dragon Gateway Museum — zoom-in portal + 3D museum */}
      <DragonGateway
        isOpen={museumOpen}
        onClose={() => setMuseumOpen(false)}
        originRect={dragonOriginRect}
        initialRoomId={museumInitialRoom}
      />

      {/* Sovereign's Path — user's own volume, same component as Inscribe */}
      {/* Sovereign's Path — always the GLOBAL volume (all 8 pillars merged,
          newest first).  Pillar-specific volumes now live on each pillar
          tab next to the Add Vibe button (see MemoryGrid.js). */}
      {showSovereignPath && (
        <SovereignsPath
          pillarId={null}
          onClose={() => setShowSovereignPath(false)}
        />
      )}
    </div>
  );
}

const SettingsModal = ({ user, onClose, updateProfile }) => {
  const navigate = useNavigate();
  const [nickname, setNickname] = useState(user.nickname || '');
  const [blissId, setBlissId] = useState(user.bliss_id || '');
  const [lifeQuote, setLifeQuote] = useState(user.life_quote || '');
  const [isPublic, setIsPublic] = useState(user.is_public);
  const [profileTag, setProfileTag] = useState(user.profile_tag || null);
  const [profilePicture, setProfilePicture] = useState(user.profile_picture || null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(user.profile_picture || null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleProfilePictureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePicture(reader.result);
        setProfilePicturePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    if (!profileTag) {
      setMessage('Please choose a Tag — it is required.');
      return;
    }
    setSaving(true);
    setMessage('');
    try {
      if (blissId && blissId.length < 3) { setMessage('Bliss ID must be at least 3 characters.'); return; }
      await updateProfile({ nickname, life_quote: lifeQuote, is_public: isPublic, profile_picture: profilePicture, profile_tag: profileTag, bliss_id: blissId || null });
      setMessage('Profile updated successfully!');
      setTimeout(() => onClose(), 1500);
    } catch (err) {
      const d = err.response?.data?.detail;
      setMessage(typeof d === 'string' ? d : 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full px-4 py-3 rounded-lg border border-[#D4AF37]/15 bg-white/5 focus:outline-none focus:ring-1 focus:ring-[#D4AF37]/50 focus:border-[#D4AF37]/30 font-['Outfit'] text-white/90 placeholder-white/20";

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4" style={{ background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(8px)' }} onClick={onClose} data-testid="settings-modal">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto border border-[#D4AF37]/15"
        style={{ background: 'linear-gradient(180deg, #0c0c14 0%, #05050A 100%)' }}
      >
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-['Cormorant_Garamond'] text-2xl font-light text-white">Settings</h3>
          <button onClick={onClose} className="text-white/30 hover:text-white transition-colors" data-testid="close-settings-modal">
            <X size={24} />
          </button>
        </div>


        {user.role === 'admin' && (
          <button
            onClick={() => { onClose(); navigate('/admin'); }}
            className="w-full mb-4 flex items-center justify-center gap-2 bg-[#D4AF37] text-black py-3 rounded-full font-['Outfit'] font-semibold hover:shadow-[0_0_20px_rgba(212,175,55,0.3)] transition-all"
            data-testid="admin-panel-button"
          >
            <Shield size={18} /> Admin Panel
          </button>
        )}

        <div className="space-y-4">
          {/* Profile Picture Upload */}
          <div className="flex flex-col items-center mb-4">
            <label className="block font-['Outfit'] text-[10px] tracking-[0.15em] uppercase font-bold text-[#D4AF37]/60 mb-3">Profile Picture</label>
            <div className="relative group cursor-pointer">
              <div className="w-28 h-28 rounded-full overflow-hidden border-2 border-[#D4AF37]/30" style={{ boxShadow: '0 0 20px rgba(212,175,55,0.15)' }}>
                {profilePicturePreview ? (
                  <img src={profilePicturePreview} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-[#D4AF37]/10 flex items-center justify-center">
                    <span className="font-['Cormorant_Garamond'] text-3xl text-[#D4AF37]">{nickname?.charAt(0).toUpperCase() || 'U'}</span>
                  </div>
                )}
              </div>
              <label htmlFor="profile-picture-input" className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                <div className="text-center text-[#D4AF37]">
                  <Camera size={28} className="mx-auto mb-1" />
                  <span className="text-xs font-['Outfit']">Change</span>
                </div>
              </label>
              <input id="profile-picture-input" type="file" accept="image/*" onChange={handleProfilePictureChange} className="hidden" data-testid="profile-picture-input" />
            </div>
          </div>

          <div>
            <label className="block font-['Outfit'] text-[10px] tracking-[0.15em] uppercase font-bold text-[#D4AF37]/60 mb-2">Nickname</label>
            <input type="text" value={nickname} onChange={(e) => setNickname(e.target.value)} className={inputCls} data-testid="settings-nickname-input" />
          </div>

          <div>
            <label className="block font-['Outfit'] text-[10px] tracking-[0.15em] uppercase font-bold text-[#D4AF37]/60 mb-2">Bliss ID (login username)</label>
            <input type="text" value={blissId} maxLength={20} autoCapitalize="none" autoCorrect="off" placeholder="your.blissid"
              onChange={(e) => setBlissId(e.target.value.toLowerCase().replace(/[^a-z0-9._]/g, ''))}
              className={inputCls} data-testid="settings-blissid-input" />
            <p className="font-['Outfit'] text-[11px] text-white/30 mt-1.5">Use it instead of your email to sign in · 3–20 characters</p>
          </div>

          {/* Tag Picker — 9 emblems (mandatory selection) */}
          <div>
            <label className="block font-['Outfit'] text-[10px] tracking-[0.15em] uppercase font-bold text-[#D4AF37]/60 mb-2">
              Tag <span className="text-red-400/80 normal-case font-semibold tracking-normal ml-1">* Required</span>
            </label>
            <div className="flex items-center gap-2 flex-wrap p-2 rounded-xl"
              style={{
                border: profileTag ? '1px solid rgba(212,175,55,0.18)' : '1px solid rgba(239,68,68,0.45)',
                boxShadow: profileTag ? 'none' : 'inset 0 0 14px rgba(239,68,68,0.18)',
                background: profileTag ? 'transparent' : 'rgba(239,68,68,0.04)',
                transition: 'all 200ms ease',
              }}
              data-testid="tag-picker">
              {['fashion','food','explore','souls','sporty','success','legacy','art','dragon'].map((key) => {
                const icon = ANIMAL_ICONS[key];
                const selected = profileTag === key;
                return (
                  <button key={key} type="button" onClick={() => setProfileTag(key)}
                    className="w-9 h-9 rounded-full overflow-hidden transition-all duration-200"
                    style={{
                      border: selected ? '2px solid #D4AF37' : '1.5px solid rgba(255,255,255,0.08)',
                      boxShadow: selected ? '0 0 12px rgba(212,175,55,0.4)' : 'none',
                      opacity: selected ? 1 : 0.5,
                    }}
                    data-testid={`tag-option-${key}`}>
                    {icon && <img src={icon.src} alt={key} className="w-full h-full object-cover" style={{ objectPosition: icon.pos }} />}
                  </button>
                );
              })}
            </div>
            {!profileTag && (
              <p className="font-['Outfit'] text-[11px] text-red-300/70 mt-1.5" data-testid="tag-required-hint">
                Choose an emblem to represent your vibe.
              </p>
            )}
          </div>
          <div>
            <label className="block font-['Outfit'] text-[10px] tracking-[0.15em] uppercase font-bold text-[#D4AF37]/60 mb-2">Life Quote</label>
            <textarea value={lifeQuote} onChange={(e) => setLifeQuote(e.target.value)} className={`${inputCls} resize-none`} rows="3" data-testid="settings-lifequote-input" />
          </div>
          <div className="flex items-center justify-between py-3 px-4 rounded-lg border border-[#D4AF37]/10" style={{ background: 'rgba(212,175,55,0.03)' }}>
            <div>
              <label className="block font-['Outfit'] text-sm font-medium text-white/70">Public Profile</label>
              <p className="font-['Outfit'] text-xs text-white/30 mt-1">Allow others to see your vibes</p>
            </div>
            <Switch checked={isPublic} onCheckedChange={setIsPublic} data-testid="toggle-public-account" />
          </div>

          <AppDescription />
          <FeaturesDescription />
        </div>

        {message && (
          <div data-testid="settings-message" className={`px-4 py-3 rounded-lg mt-4 font-['Outfit'] text-sm ${message.includes('success') ? 'bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37]' : 'bg-red-500/10 border border-red-500/30 text-red-400'}`}>
            {message}
          </div>
        )}
        <button
          onClick={handleSave}
          disabled={saving || !profileTag}
          className="w-full mt-4 bg-[#D4AF37] text-black py-3 rounded-full font-['Outfit'] font-semibold hover:shadow-[0_0_20px_rgba(212,175,55,0.3)] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="save-settings-button"
        >
          {saving ? 'Saving...' : profileTag ? 'Save Changes' : 'Choose a Tag to Save'}
        </button>
      </motion.div>
    </div>
  );
};
